import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import * as z from 'zod'
import useMutationEnable2FA from '../../api/hooks/useMutationEnable2FA'
import useQueryGetTOTPGenerate from '../../api/hooks/useQueryGetTOTPGenerate'
import { useQueryClient } from '@tanstack/react-query'
import { AuthQueryKey } from '@/modules/auth/keys'
import { useEffect } from 'react'
import { ProfileQueryKey } from '../../keys'
import { OperatorQueryKeys } from '@/modules/manage-member/keys'

const enable2FASchema = z.object({
	totpCode: z
		.string({
			required_error: 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว',
			invalid_type_error: 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว',
		})
		.min(6, 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว')
		.max(6, 'รหัส TOTP ต้องมีอย่างน้อย 6 ตัว'),
})

type Enable2FADialogProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
	id: string
}

export default function Enable2FADialog({
	open,
	onOpenChange,
	id,
}: Enable2FADialogProps) {
	const { data: totpData, refetch: refetchTOTP } = useQueryGetTOTPGenerate(id)
	const { mutate: enable2FA, isPending: isEnabling2FA } =
		useMutationEnable2FA(id)

	const queryClient = useQueryClient()

	const form = useForm<z.infer<typeof enable2FASchema>>({
		resolver: zodResolver(enable2FASchema),
	})

	const handleGenerateQR = async () => {
		await refetchTOTP()
	}

	const onSubmit = (data: z.infer<typeof enable2FASchema>) => {
		enable2FA(data, {
			onSuccess: () => {
				toast.success('เปิดใช้งาน 2FA สำเร็จ')
				form.reset()
				onOpenChange(false)
				queryClient.invalidateQueries({
					queryKey: [AuthQueryKey.Profile],
				})
				queryClient.invalidateQueries({
					queryKey: [OperatorQueryKeys.GetMemberList],
				})
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	useEffect(() => {
		if (!open) {
			form.reset()
			queryClient.removeQueries({
				queryKey: [ProfileQueryKey.GetTOTPGenerate],
			})
		}
	}, [open, form, queryClient])

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>เปิดใช้งาน Two-Factor Authentication</DialogTitle>
					<DialogDescription>
						สแกน QR Code ด้วยแอป Authenticator เพื่อเปิดใช้งาน 2FA
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					{!totpData ? (
						<Button onClick={handleGenerateQR} className="w-full">
							สร้าง QR Code
						</Button>
					) : (
						<>
							<div className="flex flex-col items-center space-y-2">
								<img
									src={totpData.qrCode}
									alt="QR Code for 2FA"
									className="h-48 w-48"
								/>
								<p className="text-sm text-gray-500">
									สแกน QR Code ด้วยแอป Authenticator
								</p>
							</div>
							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-4"
								>
									<FormField
										control={form.control}
										name="totpCode"
										render={({ field }) => (
											<FormItem>
												<FormLabel>รหัส TOTP</FormLabel>
												<FormControl>
													<Input placeholder="กรอกรหัส TOTP..." {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<DialogFooter>
										<Button type="submit" disabled={isEnabling2FA}>
											เปิดใช้งาน 2FA
										</Button>
									</DialogFooter>
								</form>
							</Form>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
